// Perfiles metrológicos de CAUDAL.
// Convención única: error = indicación del instrumento - valor corregido del patrón.

const SQRT3 = Math.sqrt(3);

export const MASS_FLOW_TEMPLATE = {
  code: 'FLOW-MASS:coriolis:v1', revision: '1', family: 'flow', subfamily: 'mass',
  profileKey: 'mass_flow_coriolis', profileLabel: 'Caudal másico · Coriolis',
  title: 'Calibración de caudalímetros másicos', objectFallback: 'Caudalímetro másico',
  unit: 'kg/min', unitLabel: '[kg/min]', pointCount: 3, repeatCount: 3,
  blockKey: 'mass_flow', functionKey: 'mass_flow', measurementMode: 'comparison',
  method: 'Por comparación directa con caudalímetro másico patrón.',
  procedure: 'P-DT-PT-12-00 (Calibración de caudalímetros)',
};

export const ELECTROMAGNETIC_FLOW_TEMPLATE = {
  code: 'FLOW-VOLUME:electromagnetic:v1', revision: '1', family: 'flow', subfamily: 'electromagnetic',
  profileKey: 'volumetric_flow_electromagnetic', profileLabel: 'Caudal volumétrico · Electromagnético',
  title: 'Calibración de caudalímetros electromagnéticos', objectFallback: 'Caudalímetro electromagnético',
  unit: 'm³/h', unitLabel: '[m³/h]', pointCount: 10, repeatCount: 2,
  blockKey: 'volumetric_flow', functionKey: 'volumetric_flow', measurementMode: 'comparison',
  method: 'Por comparación directa con caudalímetro ultrasónico patrón, en dos series de diez lecturas.',
  procedure: 'P-DT-PT-12-00 (Calibración de caudalímetros)',
};

export const MECHANICAL_TOTALIZER_FLOW_TEMPLATE = {
  code: 'FLOW-VOLUME:mechanical_totalizer:v1', revision: '1', family: 'flow', subfamily: 'mechanical_totalizer',
  profileKey: 'volumetric_flow_mechanical_totalizer', profileLabel: 'Caudal volumétrico · Mecánico / totalizador',
  title: 'Calibración de caudalímetros mecánicos y totalizadores', objectFallback: 'Caudalímetro mecánico / totalizador',
  unit: 'm³/h', unitLabel: '[m³/h]', pointCount: 10, repeatCount: 2,
  blockKey: 'volumetric_flow', functionKey: 'volumetric_flow', measurementMode: 'mechanical_totalizer',
  method: 'Por comparación directa y verificación del totalizador mediante volumen acumulado y tiempo.',
  procedure: 'P-DT-PT-12-00 (Calibración de caudalímetros)',
};

export const ULTRASONIC_FLOW_TEMPLATE = {
  code: 'FLOW-VOLUME:ultrasonic:v1', revision: '1', family: 'flow', subfamily: 'ultrasonic',
  profileKey: 'volumetric_flow_ultrasonic', profileLabel: 'Caudal volumétrico · Ultrasónico',
  title: 'Calibración de caudalímetros ultrasónicos', objectFallback: 'Caudalímetro ultrasónico',
  unit: 'm³/h', unitLabel: '[m³/h]', pointCount: 10, repeatCount: 2,
  blockKey: 'volumetric_flow', functionKey: 'volumetric_flow', measurementMode: 'comparison',
  method: 'Por comparación directa con caudalímetro ultrasónico patrón, en dos series de diez lecturas.',
  procedure: 'P-DT-PT-12-00 (Calibración de caudalímetros)',
};

export const ROTAMETER_FLOW_TEMPLATE = {
  code: 'FLOW-VOLUME:rotameter:v1', revision: '1', family: 'flow', subfamily: 'rotameter',
  profileKey: 'volumetric_flow_rotameter', profileLabel: 'Caudal volumétrico · Flotámetro',
  title: 'Calibración de flotámetros', objectFallback: 'Caudalímetro / flotámetro',
  unit: 'm³/h', unitLabel: '[m³/h]', pointCount: 6, repeatCount: 1,
  blockKey: 'volumetric_flow', functionKey: 'volumetric_flow', measurementMode: 'comparison',
  setpointFractions: [0.1, 0.2, 0.4, 0.6, 0.8, 1],
  method: 'Por comparación directa con caudalímetro patrón en seis puntos de la escala.',
  procedure: 'P-DT-PT-12-00 (Calibración de caudalímetros)',
};

export const FLOW_TEMPLATES = [
  MASS_FLOW_TEMPLATE,
  ELECTROMAGNETIC_FLOW_TEMPLATE,
  MECHANICAL_TOTALIZER_FLOW_TEMPLATE,
  ULTRASONIC_FLOW_TEMPLATE,
  ROTAMETER_FLOW_TEMPLATE,
];

export function resolveFlowTemplate(templateCode) {
  return FLOW_TEMPLATES.find((template) => template.code === templateCode) || MASS_FLOW_TEMPLATE;
}

function buildTotalizerRuns(count = 20) {
  return Array.from({ length: count }, (_, index) => ({
    run_no: index + 1,
    series_no: index < count / 2 ? 1 : 2,
    initial_total: null,
    final_total: null,
    elapsed_hours: null,
    elapsed_minutes: null,
    elapsed_seconds: null,
  }));
}

export const DEFAULT_FLOW_FAMILY_DATA = {
  profile_key: MASS_FLOW_TEMPLATE.profileKey,
  profile_label: MASS_FLOW_TEMPLATE.profileLabel,
  method: MASS_FLOW_TEMPLATE.method,
  procedure: MASS_FLOW_TEMPLATE.procedure,
  tolerance_mode: 'percent',
  decision_rule: 'simple',
  fluid: '',
  installation_position: 'Posición existente en línea',
  pipe_diameter_mm: null,
  pipe_thickness_mm: null,
  pipe_lining: '',
  upstream_straight_length_d: null,
  downstream_straight_length_d: null,
  conductivity_us_cm: null,
  transducer_model: '',
  transducer_spacing_mm: null,
  transducer_mounting: '',
  totalizer_volume_unit: 'm³',
  pulse_factor: null,
  rotameter_service: 'gas',
  reference_temperature_c: 20,
  reference_pressure_hpa: 1013.25,
  gas_density_kg_m3: null,
  standard_configuration: {},
  standard_budget: {
    drift_percent: 0,
    temperature_coefficient_percent_per_10c: 0.4,
    other_uncertainty: 0,
  },
  environment_readings: [
    { key: 'start_1', label: 'Inicio 1.er ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'start_2', label: 'Inicio 2.do ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end_2', label: 'Fin 2.do ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  totalizer_runs: [],
};

export function defaultFlowFamilyData(templateCode = MASS_FLOW_TEMPLATE.code) {
  const template = resolveFlowTemplate(templateCode);
  return {
    ...DEFAULT_FLOW_FAMILY_DATA,
    profile_key: template.profileKey,
    profile_label: template.profileLabel,
    method: template.method,
    procedure: template.procedure,
    totalizer_runs: template.measurementMode === 'mechanical_totalizer' ? buildTotalizerRuns() : [],
  };
}

export const MASS_FLOW_READING_FIELDS = [
  'r1_pattern', 'r1_instrument', 'r2_pattern', 'r2_instrument', 'r3_pattern', 'r3_instrument',
];

export function flowReadingFields(templateCode) {
  const template = resolveFlowTemplate(templateCode);
  return Array.from({ length: template.repeatCount }, (_, index) => [
    `r${index + 1}_pattern`, `r${index + 1}_instrument`,
  ]).flat();
}

export function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function normalizeFlowUnit(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '').replace(/³/g, '3').replace(/²/g, '2');
}

export const normalizeMassFlowUnit = normalizeFlowUnit;

export function massFlowToKgPerMinute(value, unit) {
  const numeric = finiteOrNull(value);
  if (numeric === null) return null;
  const normalized = normalizeFlowUnit(unit);
  if (normalized === 'kg/min') return numeric;
  if (normalized === 'kg/h') return numeric / 60;
  if (normalized === 't/h' || normalized === 'tn/h') return (numeric * 1000) / 60;
  if (normalized === 'g/min') return numeric / 1000;
  throw new Error(`La unidad ${unit || '(sin unidad)'} no es una unidad de caudal másico admitida.`);
}

export function volumetricFlowToCubicMetresPerHour(value, unit) {
  const numeric = finiteOrNull(value);
  if (numeric === null) return null;
  const normalized = normalizeFlowUnit(unit).replace(/^n(?=l|m3)/, '');
  if (normalized === 'm3/h') return numeric;
  if (normalized === 'm3/min') return numeric * 60;
  if (normalized === 'm3/s') return numeric * 3600;
  if (normalized === 'l/h') return numeric / 1000;
  if (normalized === 'l/min') return numeric * 0.06;
  if (normalized === 'l/s') return numeric * 3.6;
  if (normalized === 'ml/min') return numeric * 0.00006;
  throw new Error(`La unidad ${unit || '(sin unidad)'} no es una unidad de caudal volumétrico admitida.`);
}

export function flowToCanonical(value, unit, templateCode) {
  const template = resolveFlowTemplate(templateCode);
  return template.functionKey === 'mass_flow'
    ? massFlowToKgPerMinute(value, unit)
    : volumetricFlowToCubicMetresPerHour(value, unit);
}

function mean(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null;
}

function sampleStandardDeviation(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  if (finite.length < 2) return 0;
  const average = mean(finite);
  return Math.sqrt(finite.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (finite.length - 1));
}

function rss(values = []) {
  return Math.sqrt(values.reduce((sum, value) => sum + ((finiteOrNull(value) ?? 0) ** 2), 0));
}

function rectangularFromLimit(limit) {
  return Math.abs(finiteOrNull(limit) ?? 0) / SQRT3;
}

function resolutionStandardUncertainty(resolution) {
  return Math.abs(finiteOrNull(resolution) ?? 0) / Math.sqrt(12);
}

export function mergeFlowFamilyData(value = {}, templateCode = null) {
  const input = value && typeof value === 'object' ? value : {};
  const inferredCode = templateCode || input.template_code || FLOW_TEMPLATES.find((item) => item.profileKey === input.profile_key)?.code;
  const template = resolveFlowTemplate(inferredCode);
  const fallback = defaultFlowFamilyData(template.code);
  const budget = input.standard_budget && typeof input.standard_budget === 'object' ? input.standard_budget : {};
  const readings = Array.isArray(input.environment_readings) ? input.environment_readings : [];
  const runs = Array.isArray(input.totalizer_runs) ? input.totalizer_runs : [];
  return {
    ...fallback,
    ...input,
    profile_key: template.profileKey,
    profile_label: template.profileLabel,
    method: input.method || template.method,
    procedure: input.procedure || template.procedure,
    standard_budget: { ...fallback.standard_budget, ...budget },
    environment_readings: fallback.environment_readings.map((item, index) => ({
      ...item,
      ...(readings.find((reading) => reading?.key === item.key) || readings[index] || {}),
    })),
    totalizer_runs: template.measurementMode === 'mechanical_totalizer'
      ? buildTotalizerRuns().map((item, index) => ({ ...item, ...(runs[index] || {}) }))
      : [],
  };
}

export function buildFlowBlock(templateCode = MASS_FLOW_TEMPLATE.code) {
  const template = resolveFlowTemplate(templateCode);
  return {
    block_key: template.blockKey,
    display_name: template.profileLabel,
    unit: template.unitLabel,
    applicable: true,
    point_count: template.pointCount,
    sort_order: 0,
    mode: template.measurementMode === 'mechanical_totalizer' ? 'comparison_with_totalizer' : 'repeated_comparison',
  };
}

export function buildMassFlowBlock() {
  return buildFlowBlock(MASS_FLOW_TEMPLATE.code);
}

export function suggestFlowSetpoints(rangeMin, rangeMax, templateCode = MASS_FLOW_TEMPLATE.code) {
  const template = resolveFlowTemplate(templateCode);
  const min = finiteOrNull(rangeMin);
  const max = finiteOrNull(rangeMax);
  if (max === null || max <= (min ?? 0)) return Array(template.pointCount).fill('');
  const lower = min ?? 0;
  const fractions = template.setpointFractions || Array.from(
    { length: template.pointCount },
    (_, index) => (index + 1) / template.pointCount,
  );
  return fractions.map((fraction) => Number((lower + ((max - lower) * fraction)).toFixed(6)));
}

export function suggestMassFlowSetpoints(rangeMin, rangeMax) {
  return suggestFlowSetpoints(rangeMin, rangeMax, MASS_FLOW_TEMPLATE.code);
}

function referenceConfiguration(session = {}) {
  return mergeFlowFamilyData(session.family_data, session.template_code)?.standard_configuration?.reference || {};
}

function convertReferenceValue(value, unit, templateCode) {
  try {
    return flowToCanonical(value, unit || resolveFlowTemplate(templateCode).unit, templateCode);
  } catch {
    return null;
  }
}

export function evaluateFlowReference(value, session = {}) {
  const reading = finiteOrNull(value);
  if (reading === null) return { valid: false, reason: 'Lectura del patrón vacía.' };
  const template = resolveFlowTemplate(session.template_code);
  const reference = referenceConfiguration(session);
  const certificate = reference.certificate || {};
  const capability = reference.capability || {};
  const results = Array.isArray(reference.results) ? reference.results : [];
  if (!certificate.id || !capability.id) return { valid: false, reason: 'No hay un patrón de caudal fijado en la sesión.' };
  if (!results.length) return { valid: false, reason: 'El certificado del patrón no tiene resultados metrológicos cargados.' };

  const rangeMin = convertReferenceValue(capability.range_min, capability.unit, template.code);
  const rangeMax = convertReferenceValue(capability.range_max, capability.unit, template.code);
  if (rangeMin !== null && reading < rangeMin) return { valid: false, reason: `La lectura patrón ${reading} queda por debajo del alcance certificado (${rangeMin} ${template.unit}).` };
  if (rangeMax !== null && reading > rangeMax) return { valid: false, reason: `La lectura patrón ${reading} supera el alcance certificado (${rangeMax} ${template.unit}).` };

  const coverageFactor = finiteOrNull(certificate.coverage_factor) ?? finiteOrNull(results[0]?.coverage_factor) ?? 2;
  if (coverageFactor <= 0) return { valid: false, reason: 'El factor de cobertura del certificado no es válido.' };
  const metadata = capability.metadata || {};
  const uncertaintyMode = metadata.uncertainty_mode || results.find((item) => item?.metadata)?.metadata?.uncertainty_mode || 'absolute';
  let uncertaintyExpanded = null;
  let uncertaintyPercent = null;
  let correction = 0;

  if (uncertaintyMode === 'percent_of_reading') {
    uncertaintyPercent = finiteOrNull(metadata.certified_uncertainty_percent)
      ?? Math.max(...results.map((item) => finiteOrNull(item.uncertainty_expanded)).filter((item) => item !== null));
    if (Number.isFinite(uncertaintyPercent)) uncertaintyExpanded = Math.abs(reading) * Math.abs(uncertaintyPercent) / 100;
  } else {
    const candidates = results.map((item) => ({
      item,
      reference: convertReferenceValue(item.reference_value, item.unit || capability.unit, template.code),
    })).filter((item) => item.reference !== null);
    candidates.sort((a, b) => Math.abs(a.reference - reading) - Math.abs(b.reference - reading));
    const nearest = candidates[0]?.item;
    if (nearest) {
      uncertaintyExpanded = convertReferenceValue(nearest.uncertainty_expanded, nearest.unit || capability.unit, template.code);
      correction = convertReferenceValue(nearest.correction, nearest.unit || capability.unit, template.code) ?? 0;
    }
  }
  if (!Number.isFinite(uncertaintyExpanded)) return { valid: false, reason: 'El patrón no tiene una incertidumbre certificada utilizable.' };
  return {
    valid: true,
    raw_value: reading,
    corrected_value: reading + correction,
    correction,
    uncertainty_expanded: Math.abs(uncertaintyExpanded),
    uncertainty_percent: uncertaintyPercent === null ? null : Math.abs(uncertaintyPercent),
    coverage_factor: coverageFactor,
    standard_uncertainty: Math.abs(uncertaintyExpanded) / coverageFactor,
  };
}

export function evaluateMassFlowReference(value, session = {}) {
  return evaluateFlowReference(value, { ...session, template_code: session.template_code || MASS_FLOW_TEMPLATE.code });
}

export function computeFlowPointMetrics(row = {}, session = {}) {
  const template = resolveFlowTemplate(session.template_code);
  const pairs = Array.from({ length: template.repeatCount }, (_, index) => ({
    pattern: finiteOrNull(row[`r${index + 1}_pattern`]),
    instrument: finiteOrNull(row[`r${index + 1}_instrument`]),
  })).filter((pair) => pair.pattern !== null && pair.instrument !== null);
  if (!pairs.length) return null;
  const evaluations = pairs.map((pair) => evaluateFlowReference(pair.pattern, session));
  const invalid = evaluations.find((evaluation) => !evaluation.valid);
  if (invalid) return { traceability_issue: invalid.reason, pattern_mean: mean(pairs.map((pair) => pair.pattern)), instrument_mean: mean(pairs.map((pair) => pair.instrument)) };

  const patternValues = evaluations.map((evaluation) => evaluation.corrected_value);
  const instrumentValues = pairs.map((pair) => pair.instrument);
  const errors = instrumentValues.map((reading, index) => reading - patternValues[index]);
  const patternMean = mean(patternValues);
  const instrumentMean = mean(instrumentValues);
  const errorAbs = instrumentMean - patternMean;
  const errorPct = patternMean === 0 ? null : (errorAbs / patternMean) * 100;
  const repeatability = sampleStandardDeviation(errors);
  const uRepeatability = repeatability / Math.sqrt(pairs.length);
  const familyData = mergeFlowFamilyData(session.family_data, template.code);
  const budget = familyData.standard_budget;
  const reference = referenceConfiguration(session);
  const uPatternCalibration = Math.max(...evaluations.map((evaluation) => evaluation.standard_uncertainty));
  const referenceResolution = convertReferenceValue(reference?.capability?.resolution, reference?.capability?.unit, template.code);
  const uPatternResolution = resolutionStandardUncertainty(referenceResolution);
  const uInstrumentResolution = resolutionStandardUncertainty(session.instrument_resolution);
  const uDrift = rectangularFromLimit(Math.abs(patternMean) * Math.abs(finiteOrNull(budget.drift_percent) ?? 0) / 100);
  const ambientDelta = Math.abs((finiteOrNull(session.ambient_temp_avg) ?? 20) - 20);
  const tempLimit = Math.abs(patternMean) * Math.abs(finiteOrNull(budget.temperature_coefficient_percent_per_10c) ?? 0) * ambientDelta / 1000;
  const uTemperature = rectangularFromLimit(tempLimit);
  const uOther = rectangularFromLimit(budget.other_uncertainty);
  const uStandard = rss([uPatternCalibration, uPatternResolution]);
  const uc = rss([uStandard, uDrift, uInstrumentResolution, uRepeatability, uTemperature, uOther]);
  const k = 2;
  const uExpanded = uc * k;
  const tolerance = finiteOrNull(session.instrument_class);
  let inTolerance = null;
  if (tolerance !== null && tolerance >= 0) {
    inTolerance = familyData.tolerance_mode === 'absolute'
      ? Math.abs(errorAbs) <= tolerance
      : errorPct !== null && Math.abs(errorPct) <= tolerance;
  }
  return {
    pattern_mean: patternMean, instrument_mean: instrumentMean, error_abs: errorAbs, error_pct: errorPct,
    hysteresis: null, repeatability, u_standard: uStandard, u_drift: uDrift,
    u_resolution: uInstrumentResolution, u_repeatability: uRepeatability, u_temp: uTemperature,
    u_other: uOther, uc, k, u_expanded: uExpanded, u_reported: Math.max(uExpanded, Math.abs(Number(session.instrument_resolution) || 0)), in_tolerance: inTolerance, // piso por resolucion del instrumento (QA 2026-09-06)
    uncertainty_components: {
      pattern_calibration: uPatternCalibration, pattern_resolution: uPatternResolution,
      instrument_resolution: uInstrumentResolution, pattern_drift: uDrift,
      temperature_influence: uTemperature, repeatability: uRepeatability, other: uOther,
      reference_certificate_uncertainty_percent: evaluations[0]?.uncertainty_percent,
      reference_coverage_factor: evaluations[0]?.coverage_factor, repeat_count: pairs.length,
    },
  };
}

export function computeMassFlowPointMetrics(row = {}, session = {}) {
  return computeFlowPointMetrics(row, { ...session, template_code: session.template_code || MASS_FLOW_TEMPLATE.code });
}

export function computeTotalizerRun(run = {}, familyData = {}) {
  const initial = finiteOrNull(run.initial_total);
  const final = finiteOrNull(run.final_total);
  const elapsedSeconds = ((finiteOrNull(run.elapsed_hours) ?? 0) * 3600)
    + ((finiteOrNull(run.elapsed_minutes) ?? 0) * 60)
    + (finiteOrNull(run.elapsed_seconds) ?? 0);
  if (initial === null || final === null || elapsedSeconds <= 0) return null;
  const delta = final - initial;
  let deltaM3;
  const unit = normalizeFlowUnit(familyData.totalizer_volume_unit || 'm³');
  if (unit === 'm3') deltaM3 = delta;
  else if (unit === 'l' || unit === 'litro' || unit === 'litros') deltaM3 = delta / 1000;
  else return { valid: false, issue: 'La unidad del totalizador debe ser m³ o L.' };
  return { valid: delta >= 0, issue: delta < 0 ? 'La lectura final no puede ser menor que la inicial.' : null, delta, elapsed_seconds: elapsedSeconds, calculated_flow_m3_h: deltaM3 * 3600 / elapsedSeconds };
}
